import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';

export type CredentialLevel = 'exhortador' | 'licenciado' | 'ordenado';
export interface AdCredential {
  personId: string;
  name: string;
  level: CredentialLevel;
  number: string | null;
  issuedOn: string | null;
  expiresOn: string | null;
  district: string | null;
  notes: string | null;
  status: 'vigente' | 'por_vencer' | 'vencida';
}

export const LEVELS: Record<CredentialLevel, string> = { exhortador: 'Exhortador', licenciado: 'Ministro licenciado', ordenado: 'Ministro ordenado' };
const STATUS: Record<AdCredential['status'], { label: string; badge: string }> = {
  vigente: { label: 'Vigente', badge: 'badge--active' },
  por_vencer: { label: 'Por vencer', badge: 'badge--pending' },
  vencida: { label: 'Vencida', badge: 'badge--warn' },
};
const day = (iso: string | null) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

/** Asambleas de Dios · credenciales ministeriales de la iglesia y su vencimiento. */
export function AdCredentialsPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const base = `/multichurch/churches/${churchId}/ad/credentials`;
  const canManage = me.can('members.manage');
  const list = useAsync((signal) => (churchId ? api.get<{ items: AdCredential[] }>(base, signal).then((r) => r.items) : Promise.resolve([])), [base]);
  const people = useAsync(
    (signal) =>
      churchId && canManage
        ? api.get<{ items: { personId: string; givenName: string; familyName: string }[] }>(`/multichurch/churches/${churchId}/members`, signal).then((r) => r.items)
        : Promise.resolve([]),
    [churchId, canManage],
  );
  const [editing, setEditing] = useState<AdCredential | 'new' | null>(null);

  if (!churchId) return <div className="page"><EmptyState title="Elige primero una iglesia en el selector de arriba." /></div>;
  const items = list.status === 'ready' ? list.data : [];
  const due = items.filter((c) => c.status !== 'vigente');
  const editingId = editing && editing !== 'new' ? editing.personId : null;
  const done = () => {
    setEditing(null);
    list.reload();
  };

  return (
    <div className="page">
      <header className="page__header page__header--actions">
        <div>
          <p className="eyebrow">Asambleas de Dios</p>
          <h1>Credenciales ministeriales</h1>
          <p className="muted">Exhortadores, ministros licenciados y ordenados de la iglesia, con su número y vencimiento. Te avisamos 60 días antes de que venzan.</p>
        </div>
        {canManage && !editing ? <Button onClick={() => setEditing('new')}>Registrar credencial</Button> : null}
      </header>

      {due.length ? (
        <Alert tone="warning">
          {due.length === 1 ? '1 credencial vencida o por vencer' : `${due.length} credenciales vencidas o por vencer`}: {due.map((c) => c.name).join(', ')}.
        </Alert>
      ) : null}

      {editing === 'new' ? (
        <CredentialForm
          key="new"
          base={base}
          current={null}
          people={(people.status === 'ready' ? people.data : []).filter((p) => !items.some((c) => c.personId === p.personId))}
          onDone={done}
        />
      ) : null}

      {list.status === 'loading' ? <Loading /> : null}
      {list.status === 'error' ? <ErrorState error={list.error} onRetry={list.reload} /> : null}
      {list.status === 'ready' && items.length === 0 ? <EmptyState title="Todavía no hay credenciales registradas." /> : null}
      {items.length ? (
        <ul className="hub">
          {items.map((c) => (
            <li key={c.personId}>
              <div className="hub__item ad-credential">
                <span>
                  <strong>
                    <Link to={`/app/personas/${c.personId}`}>{c.name}</Link>
                  </strong>
                  <span>{LEVELS[c.level]}</span>
                  <span className="small muted">
                    {[c.number ? `No. ${c.number}` : null, c.district, c.expiresOn ? `vence ${day(c.expiresOn)}` : 'sin vencimiento'].filter(Boolean).join(' · ')}
                  </span>
                  <span>
                    <span className={`badge ${STATUS[c.status].badge}`}>{STATUS[c.status].label}</span>
                  </span>
                </span>
                {canManage && editingId !== c.personId ? (
                  <Button variant="ghost" onClick={() => setEditing(c)}>
                    Editar
                  </Button>
                ) : null}
              </div>
              {editingId === c.personId ? <CredentialForm key={c.personId} base={base} current={c} people={[]} onDone={done} /> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function CredentialForm({ base, current, people, onDone }: { base: string; current: AdCredential | null; people: { personId: string; givenName: string; familyName: string }[]; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const t = (k: string) => String(f.get(k) ?? '').trim() || null;
    const personId = current?.personId ?? t('personId');
    if (!personId) {
      setError('Elige a la persona.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.put(`${base}/${personId}`, { level: t('level'), number: t('number'), issuedOn: t('issuedOn'), expiresOn: t('expiresOn'), district: t('district'), notes: t('notes') });
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!current || !window.confirm(`¿Quitar la credencial de ${current.name}?`)) return;
    setBusy(true);
    try {
      await api.delete(`${base}/${current.personId}`);
      onDone();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Card title={current ? `Credencial de ${current.name}` : 'Registrar credencial'}>
      <form className="form" onSubmit={save} noValidate>
        {error ? <Alert>{error}</Alert> : null}
        {current ? null : (
          <div className="field">
            <label htmlFor="cred-person">Persona</label>
            <select id="cred-person" name="personId" defaultValue="">
              <option value="">Elige…</option>
              {people
                .map((p) => ({ id: p.personId, name: `${p.givenName} ${p.familyName}`.trim() }))
                .sort((a, b) => a.name.localeCompare(b.name, 'es'))
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </select>
          </div>
        )}
        <div className="form__row">
          <div className="field">
            <label htmlFor="cred-level">Credencial</label>
            <select id="cred-level" name="level" defaultValue={current?.level ?? 'exhortador'}>
              {Object.entries(LEVELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <Field label="Número de credencial" name="number" maxLength={40} defaultValue={current?.number ?? ''} />
        </div>
        <div className="form__row">
          <Field label="Expedida el" name="issuedOn" type="date" defaultValue={current?.issuedOn ?? ''} />
          <Field label="Vence el" name="expiresOn" type="date" defaultValue={current?.expiresOn ?? ''} />
        </div>
        <Field label="Distrito" name="district" maxLength={120} defaultValue={current?.district ?? ''} />
        <Field label="Notas" name="notes" maxLength={1000} defaultValue={current?.notes ?? ''} />
        <div className="actions actions--wrap">
          <Button type="submit" busy={busy}>
            Guardar
          </Button>
          <Button type="button" variant="ghost" onClick={onDone}>
            Cancelar
          </Button>
          {current ? (
            <Button type="button" variant="ghost" busy={busy} onClick={() => void remove()}>
              Quitar credencial
            </Button>
          ) : null}
        </div>
      </form>
    </Card>
  );
}
